/**
 * RetryButton Component
 *
 * Displays retry button with "Regenerate (X/Y) - FREE" format.
 * Highlights the FREE retry feature as competitive advantage.
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - Tailwind CSS core utilities only
 * - Format: "Regenerate (X/Y) - FREE"
 * - Green "FREE" badge to emphasize value
 */

import React from 'react';
import { OrderDocument, OrderStatus } from '@/shared/types';
import { Button } from '@/components/ui';

export interface RetryButtonProps {
  order: OrderDocument;
  onRetry: (orderId: string) => Promise<void>;
  loading?: boolean;
  className?: string;
}

/**
 * RetryButton Component
 *
 * @param order - Order document
 * @param onRetry - Retry callback
 * @param loading - Loading state
 * @param className - Additional CSS classes
 */
export function RetryButton({
  order,
  onRetry,
  loading = false,
  className = '',
}: RetryButtonProps): React.JSX.Element | null {
  const currentAttempt = order.attempts.length;
  const maxAttempts = order.maxAttempts;
  const canRetry = currentAttempt < maxAttempts && order.status !== OrderStatus.PROCESSING;

  // Don't render if no retries available
  if (!canRetry) {
    return null;
  }

  const handleRetry = async (): Promise<void> => {
    try {
      await onRetry(order.orderId);
    } catch (error) {
      console.error('Retry error:', error);
    }
  };

  return (
    <Button
      variant="primary"
      onClick={handleRetry}
      disabled={loading}
      loading={loading}
      className={`bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 ${className}`}
    >
      <span className="flex items-center space-x-2">
        <span>Regenerate ({currentAttempt}/{maxAttempts})</span>
        <span className="px-2 py-0.5 bg-white bg-opacity-30 rounded-full text-xs font-bold">
          FREE
        </span>
      </span>
    </Button>
  );
}
